// The asset store: types, pure helpers (filters, params, manifest diff, install snippets) and the client for the
// store service (`backend/store`, :8797, proxied at /api/store/v1). Pages only see the shapes defined here; the
// service's raw answers are normalised in `store-client.ts`.

export type ReviewStatus = "uploading" | "pending" | "listed" | "rejected" | "yanked";

export const ASSET_KINDS = ["character", "clothing", "prop", "vehicle", "environment", "model", "material", "effect", "generator", "behaviour", "audio"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_KIND_LABEL: Record<string, string> = {
  character: "Characters",
  clothing: "Clothing",
  prop: "Props",
  vehicle: "Vehicles",
  environment: "Environments",
  model: "Models",
  material: "Materials",
  effect: "Effects",
  generator: "Generators",
  behaviour: "Behaviours",
  audio: "Audio",
};

/** Conventions an asset can follow (asset-protocol.md section 7). */
export const CONVENTION_LABEL: Record<string, string> = {
  "thrdm.humanoid/1": "Humanoid rig",
  "thrdm.vehicle/1": "Vehicle rig",
};

export const LICENCE_LABEL: Record<string, { name: string; text: string }> = {
  personal: { name: "Personal", text: "Use it in non-commercial projects." },
  commercial: { name: "Commercial", text: "Use it in any number of games you ship, free or paid. Reselling the asset itself isn't allowed." },
  exclusive: { name: "Exclusive", text: "Sold once, then taken off the store." },
};

export interface ParamSchema {
  type?: string;
  minimum?: number;
  maximum?: number;
  default?: unknown;
  description?: string;
  format?: string;
  enum?: unknown[];
}

export interface Manifest {
  protocol?: string;
  id: string;
  version: string;
  title: string;
  kind: string;
  tags?: string[];
  author: { handle: string; verified?: boolean; id?: string };
  licence: { tier: string; file?: string };
  price?: number | null;
  cosmetic?: boolean;
  conventions?: string[];
  wear?: { slot: string };
  /** `order`: the creator's order for the params (the store keeps it in the signed asset.json; views add `params_order`) */
  params?: { type?: string; properties?: Record<string, ParamSchema>; order?: string[] };
  deterministic?: boolean;
  engines?: Record<string, { entry: string; peer?: Record<string, string> }>;
  bake?: { default: string; bakeable?: boolean };
  behaviours?: string[];
  dependencies?: Record<string, string>;
  capabilities?: string[];
  physics?: unknown;
  budget?: { triangles?: number; textureMB?: number; drawCalls?: number };
  files?: Record<string, string>;
  [k: string]: unknown;
}

export interface Stats {
  triangles?: number;
  drawCalls?: number;
  /** total package bytes */
  bytes?: number;
}

export interface Check {
  name: string;
  /** "pass" | "fail" | "warn" | "skip" */
  result: "pass" | "fail" | "warn" | "skip";
  detail?: string;
}

export interface AssetVersion {
  version: string;
  status: ReviewStatus;
  /** unix seconds */
  publishedAt: number;
  /** the moderator's note on a rejection (or approval) */
  note?: string | null;
  /** why it was yanked, by whom: shown to the creator and on the public versions list */
  yankReason?: string | null;
  checks?: Check[];
  signed?: boolean;
  stats?: Stats;
  /** URL of the package's files (ending in "/"): asset.json, preview.png, baked/…, dist/… */
  base: string;
  manifest?: Manifest;
  /** the render reviewer's job for this version (insiders: the creator, moderators) */
  renderJob?: RenderJob | null;
}

/** The store's render job for a version in review (docs/backend/store.md "Render reviewer"). */
export interface RenderJob {
  state: "queued" | "running" | "failed" | "done";
  attempts: number;
  maxAttempts?: number;
  worker: string | null;
  error: string | null;
  /** unix seconds */
  queuedAt: number | null;
  leaseUntil: number | null;
  retryAfter: number | null;
}

export interface RenderQueue {
  counts: { queued: number; running: number; failed: number; done: number };
  /** by version ref, "creator/name@1.2.0" */
  jobs: Map<string, RenderJob>;
}

/** A render job in words, for creators and moderators. `now` in unix seconds. */
export function renderJobText(j: RenderJob, now = Math.floor(Date.now() / 1000)): { text: string; cls: "ok" | "warn" | "bad" | "dim" } {
  const tries = j.maxAttempts ? ` (try ${Math.max(1, j.attempts)} of ${j.maxAttempts})` : "";
  if (j.state === "done") return { text: "Render check finished", cls: "ok" };
  if (j.state === "running") return { text: `Rendering now${tries}`, cls: "dim" };
  if (j.state === "failed") return { text: `Render check couldn't run${j.error ? `: ${j.error}` : ""}`, cls: "bad" };
  if (j.attempts > 0) {
    const wait = j.retryAfter && j.retryAfter > now ? ` in ${Math.max(1, Math.round((j.retryAfter - now) / 60))} min` : " soon";
    return { text: `Waiting to render again${wait}, after: ${j.error ?? "the last try failed"}${tries}`, cls: "warn" };
  }
  return { text: "Waiting for the render check", cls: "dim" };
}

export interface AssetSummary {
  /** creator/name */
  id: string;
  title: string;
  kind: string;
  tags: string[];
  conventions: string[];
  creator: { handle: string; name?: string; verified?: boolean; creatorId?: string | null };
  /** whole coins; 0 = free */
  price: number;
  licence: string;
  cosmetic: boolean;
  /** latest listed version */
  version: string;
  previewUrl: string | null;
  stats?: Stats;
  updatedAt?: number;
}

export interface AssetDetail extends AssetSummary {
  manifest: Manifest;
  base: string;
  /**
   * The store's public low-detail model of the latest version (its `preview_glb`), as the site's preview route
   * ("/api/store-preview/<id>/<version>/", serving preview.glb and a code-free asset.json). Paid assets show it in 3D.
   */
  previewModel?: string | null;
  versions: AssetVersion[];
  downloads?: number;
}

export interface ReviewItem {
  id: string;
  version: string;
  title: string;
  kind: string;
  creator: { handle: string; name?: string };
  submittedAt: number;
  checks: Check[];
  manifest: Manifest;
  previous: { version: string; manifest: Manifest } | null;
  base: string;
  previewUrl: string | null;
  stats?: Stats;
  /** a new creator's script asset needs a human read of the code (asset-protocol.md section 8) */
  hasCode: boolean;
  /** no render report yet: approving needs one, or a note saying why it was skipped */
  renderMissing?: boolean;
  renderJob?: RenderJob | null;
}

// ---- filters ---------------------------------------------------------------------------------------------------

export interface AssetFilter {
  q?: string;
  kind?: string | null;
  convention?: string | null;
  price?: "free" | "paid" | null;
  sort?: "new" | "title" | "price";
}

export function assetFilterFromQuery(q: URLSearchParams): AssetFilter {
  const price = q.get("price");
  const sort = q.get("sort");
  return {
    q: q.get("q") ?? undefined,
    kind: q.get("kind"),
    convention: q.get("convention"),
    price: price === "free" || price === "paid" ? price : null,
    sort: sort === "title" || sort === "price" ? sort : "new",
  };
}

export function assetFilterToQuery(f: AssetFilter): string {
  const q = new URLSearchParams();
  if (f.q?.trim()) q.set("q", f.q.trim());
  if (f.kind) q.set("kind", f.kind);
  if (f.convention) q.set("convention", f.convention);
  if (f.price) q.set("price", f.price);
  if (f.sort && f.sort !== "new") q.set("sort", f.sort);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Every word must match the title, creator, kind or a tag (the service searches server-side; the fake uses this). */
export function filterAssets(list: AssetSummary[], f: AssetFilter): AssetSummary[] {
  const words = (f.q ?? "").toLowerCase().split(/\s+/).filter(Boolean);
  const out = list.filter((a) => {
    if (f.kind && a.kind !== f.kind) return false;
    if (f.convention && !a.conventions.includes(f.convention)) return false;
    if (f.price === "free" && a.price > 0) return false;
    if (f.price === "paid" && a.price === 0) return false;
    const hay = [a.title, a.id, a.creator.handle, a.creator.name ?? "", a.kind, ...a.tags].join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  if (f.sort === "title") out.sort((a, b) => a.title.localeCompare(b.title));
  else if (f.sort === "price") out.sort((a, b) => a.price - b.price || a.title.localeCompare(b.title));
  else out.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0) || a.title.localeCompare(b.title));
  return out;
}

// ---- ids and links ---------------------------------------------------------------------------------------------

const ID_RE = /^[a-z0-9][a-z0-9-]{0,63}\/[a-z0-9][a-z0-9-]{0,63}$/;
export const isAssetId = (id: string): boolean => ID_RE.test(id);
export const assetPath = (id: string): string => `/store/a/${id}`;
export const creatorStorePath = (handle: string): string => `/store/c/${encodeURIComponent(handle)}`;
/** money's item id for a store package (licences and cosmetics share it; the kind tells them apart) */
export const moneyItemId = (id: string): string => `a_${id}`;
/** the catalogue creator a store handle publishes under (backend/store: creator = cr_<handle>) */
export const creatorIdOf = (handle: string): string => `cr_${handle}`;

// ---- params ----------------------------------------------------------------------------------------------------

export type Value = string | number | boolean;

export type Control =
  | { key: string; label: string; kind: "range"; min: number; max: number; step: number; value: number; hint?: string }
  | { key: string; label: string; kind: "toggle"; value: boolean; hint?: string }
  | { key: string; label: string; kind: "colour"; value: string; hint?: string }
  | { key: string; label: string; kind: "choice"; options: string[]; value: string; hint?: string }
  | { key: string; label: string; kind: "text"; value: string; hint?: string };

/** Params a wearer supplies (an avatar passes its own height, build, face…); not offered as sliders for clothing. */
export function isWearerParam(p: ParamSchema): boolean {
  return /avatars? (pass|measure)/i.test(p.description ?? "");
}

/** "noseWidth" → "Nose width", "headTop" → "Head top". */
export function paramLabel(key: string): string {
  const s = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The JSON Schema of `params` → form controls. Wearer params are left out when `skipWearer`. */
export function controlsFor(m: Pick<Manifest, "params">, values: Record<string, Value> = {}, skipWearer = false): Control[] {
  const out: Control[] = [];
  for (const [key, p] of orderedParams(m)) {
    if (skipWearer && isWearerParam(p)) continue;
    const label = paramLabel(key);
    const hint = p.description?.replace(/\s*\(avatars?[^)]*\)/i, "") || undefined;
    const v = values[key] ?? (p.default as Value | undefined);
    if (Array.isArray(p.enum) && p.enum.length) {
      const options = p.enum.map(String);
      out.push({ key, label, kind: "choice", options, value: String(v ?? options[0]), hint });
    } else if (p.type === "boolean") {
      out.push({ key, label, kind: "toggle", value: Boolean(v), hint });
    } else if ((p.type === "number" || p.type === "integer") && typeof p.minimum === "number" && typeof p.maximum === "number") {
      const step = p.type === "integer" ? 1 : niceStep(p.maximum - p.minimum);
      out.push({ key, label, kind: "range", min: p.minimum, max: p.maximum, step, value: typeof v === "number" ? v : p.minimum, hint });
    } else if (p.type === "string" && p.format === "color") {
      out.push({ key, label, kind: "colour", value: typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v : "#808080", hint });
    } else if (p.type === "string") {
      out.push({ key, label, kind: "text", value: String(v ?? ""), hint });
    }
  }
  return out;
}

/** The params in the creator's order (`params.order`); keys it doesn't name follow in the schema's own order. */
export function orderedParams(m: Pick<Manifest, "params">): [string, ParamSchema][] {
  const props = m.params?.properties ?? {};
  const order = Array.isArray(m.params?.order) ? m.params!.order!.filter((k) => typeof k === "string" && k in props) : [];
  const rest = Object.keys(props).filter((k) => !order.includes(k));
  return [...new Set([...order, ...rest])].map((k) => [k, props[k]!]);
}

function niceStep(span: number): number {
  const raw = span / 100;
  const pow = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? raw;
}

/** Round to the slider's step so values and snippets don't show float noise. */
export function snap(value: number, step: number): number {
  const d = Math.max(0, -Math.floor(Math.log10(step)));
  return Number((Math.round(value / step) * step).toFixed(d));
}

/** Only the params that differ from the defaults (what an install snippet needs). */
export function changedParams(m: Pick<Manifest, "params">, values: Record<string, Value>): Record<string, Value> {
  const props = m.params?.properties ?? {};
  const out: Record<string, Value> = {};
  for (const [k, v] of Object.entries(values)) if (k in props && props[k]!.default !== v) out[k] = v;
  return out;
}

// ---- stats -----------------------------------------------------------------------------------------------------

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fmtCount(n: number): string {
  return n >= 10_000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : n.toLocaleString("en-US");
}

// ---- install instructions ----------------------------------------------------------------------------------------

export interface InstallSnippets {
  mcp: string;
  mcpConfig: string;
  three: string;
  /** the thrdm CLI's verified install */
  cli: string;
}

/** The store API through this site's own proxy (`<site>/api/store/v1`), for when the store doesn't say where it is. */
export const SITE_STORE_PATH = "/api/store/v1";
export const siteStoreApi = (siteOrigin: string): string => siteOrigin.replace(/\/$/, "") + SITE_STORE_PATH;

/** The store's three.js runtime a game imports `loadAsset` from: `<store>/v1/runtime/three-<version>.js`. */
export interface RuntimeInfo {
  version: string;
  url: string;
}

/**
 * The store API base a runtime URL was served from (`…/v1/runtime/three-1.0.0.js` → `…/v1`): the store's own public
 * address in every environment. Unknown: this site's proxy, so nothing points at a fixed host (staging B4).
 */
export function storeApiOf(runtime: RuntimeInfo | null | undefined, siteOrigin: string): string {
  const m = runtime ? /^(https?:\/\/[^/]+(?:\/[^?#]*?)?\/v1)\/runtime\//.exec(runtime.url) : null;
  return m ? m[1]! : siteStoreApi(siteOrigin);
}

/** A value for a POSIX shell, single-quoted. */
const shq = (s: string) => "'" + s.split("'").join("'\\''") + "'";

/**
 * How a developer uses the asset: their agent installs it over MCP (the store's `install` tool: it verifies the store's
 * signature and every file hash, and asks before buying) or `thrdm install` does the same from a terminal, then three.js
 * code loads it from assets/ with the store's runtime. A paid install is never pre-confirmed: neither snippet buys.
 */
export function installSnippets(id: string, params: Record<string, Value>, opts: { physics?: boolean; paid?: boolean; runtime?: RuntimeInfo | null; site: string }): InstallSnippets {
  const name = id.split("/")[1] ?? id;
  const varName = name.replace(/-([a-z0-9])/g, (_, c: string) => c.toUpperCase()).replace(/^[^a-z_]+/i, "") || "asset";
  const p = Object.keys(params).length ? JSON.stringify(params) : null;
  const api = storeApiOf(opts.runtime, opts.site);
  // a paid install without confirm changes nothing and returns the price; the agent asks the user before confirming
  const mcp = `install({ id: "${id}"${p ? `, params: ${p}` : ""} })`;
  const env: Record<string, string> = { THREEDOM_STORE_URL: api };
  if (opts.paid) env.THREEDOM_SESSION = "<your session token>";
  const mcpConfig = JSON.stringify({ mcpServers: { "threedom-store": { command: "bun", args: ["<thrdm>/store/src/mcp.js"], env } } }, null, 2);
  const runtimeUrl = opts.runtime?.url ?? `${api}/runtime/three-<version>.js`;
  const lines = [
    `import * as THREE from "three";`,
    `import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";`,
    `import { loadAsset } from "${runtimeUrl}";`,
    ``,
    `const ${varName} = await loadAsset({`,
    `  THREE, GLTFLoader,`,
    `  base: "/assets/${id}/",${p ? `\n  params: ${p},` : ""}`,
    `  seed: 1,${opts.physics ? `\n  provide: { physics: { RAPIER, world } }, // optional: its colliders` : ""}`,
    `});`,
    `scene.add(${varName}.node);`,
    `// later: ${varName}.dispose();`,
  ];
  // always name the store: the CLI's built-in default is a local dev address
  const cli = [`export THREEDOM_STORE_URL=${api}`, `thrdm install ${id}${p ? ` --params ${shq(p)}` : ""}`];
  return { mcp, mcpConfig, three: lines.join("\n"), cli: cli.join("\n") };
}

// ---- manifest diff (review queue) --------------------------------------------------------------------------------

export type DiffKind = "added" | "removed" | "changed";
export interface DiffLine {
  path: string;
  kind: DiffKind;
  before?: string;
  after?: string;
  /** a reviewer should look: new capabilities, code, dependencies, licence or price changes */
  flag?: string;
}

const FLAGS: [RegExp, string][] = [
  [/^capabilities/, "new capability"],
  [/^engines\./, "code entry"],
  [/^files\.dist\//, "code changed"],
  [/^dependencies/, "dependency"],
  [/^licence/, "licence"],
  [/^price$/, "price"],
  [/^author/, "author"],
  [/^cosmetic$|^wear/, "wearable"],
];

function flat(v: unknown, prefix: string, out: Map<string, string>): void {
  if (Array.isArray(v)) {
    // arrays of scalars compare as sets (capabilities, tags, conventions)
    if (v.every((x) => typeof x !== "object" || x === null)) {
      for (const x of v) out.set(`${prefix}[${String(x)}]`, JSON.stringify(x));
      return;
    }
    v.forEach((x, i) => flat(x, `${prefix}[${i}]`, out));
    return;
  }
  if (v && typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) out.set(prefix, "{}");
    for (const [k, x] of entries) flat(x, prefix ? `${prefix}.${k}` : k, out);
    return;
  }
  out.set(prefix, JSON.stringify(v));
}

/** Field-by-field diff of two manifests. `version` itself is left out (it always changes). */
export function manifestDiff(before: Manifest | null, after: Manifest): DiffLine[] {
  const a = new Map<string, string>();
  const b = new Map<string, string>();
  if (before) flat({ ...before, version: undefined }, "", a);
  flat({ ...after, version: undefined }, "", b);
  a.delete("version");
  b.delete("version");
  const keys = [...new Set([...a.keys(), ...b.keys()])].sort();
  const out: DiffLine[] = [];
  for (const path of keys) {
    const x = a.get(path);
    const y = b.get(path);
    if (x === y) continue;
    const kind: DiffKind = x === undefined ? "added" : y === undefined ? "removed" : "changed";
    const flag = before ? FLAGS.find(([re]) => re.test(path))?.[1] : undefined;
    // a removed capability is never a concern
    const line: DiffLine = { path, kind, before: x, after: y };
    if (flag && !(flag === "new capability" && kind === "removed")) line.flag = flag;
    out.push(line);
  }
  return out;
}

// ---- statuses ----------------------------------------------------------------------------------------------------

export const STATUS_LABEL: Record<ReviewStatus, { text: string; cls: string }> = {
  uploading: { text: "Uploading", cls: "" },
  pending: { text: "In review", cls: "warn" },
  listed: { text: "Listed", cls: "ok" },
  rejected: { text: "Not approved", cls: "bad" },
  yanked: { text: "Yanked", cls: "" },
};

/** Only a listed version can be yanked (the store service's rule); a pending one is left to review. */
export const canYank = (v: Pick<AssetVersion, "status">): boolean => v.status === "listed";

export function checkSummary(checks: Check[]): { pass: number; fail: number; warn: number } {
  return {
    pass: checks.filter((c) => c.result === "pass").length,
    fail: checks.filter((c) => c.result === "fail").length,
    warn: checks.filter((c) => c.result === "warn").length,
  };
}
